import React, { useEffect, useState } from 'react';
import CategoriaIcone from './CategoriaIcone';

interface Edital {
  id?: string;
  nome?: string;
  processoId?: string;
  processoNome?: string;
  candidatos?: any[];
  categoria?: string;
}

type EditalOrString = Edital | string;

interface EditaisProcessoModalProps {
  show: boolean;
  onHide: () => void;
  editais: EditalOrString[];
  processoNome: string;
}

const EditaisProcessoModal: React.FC<EditaisProcessoModalProps> = ({
  show,
  onHide,
  editais,
  processoNome
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const categoria = editais.length > 0 && typeof editais[0] !== 'string' 
    ? (editais[0] as Edital).categoria 
    : undefined;

  const formatarCategoria = (cat?: string): string => {
    if (!cat) return '';
    const mapa: Record<string, string> = {
      ALUNO_TECNICO: 'Aluno Técnico',
      ALUNO_GRADUACAO: 'Aluno Graduação',
      ALUNO_POS: 'Aluno Pós-graduação',
      SERVIDOR: 'Servidor',
      DOCENTE: 'Docente',
      PROCURADOR: 'Procurador',
    };
    return mapa[cat] || cat;
  };

  useEffect(() => {
    if (show) {
      setIsVisible(true);
    }
  }, [show]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onHide, 200);
  };

  if (!show) return null;

  return (
    <>
      <style>
        {`
          @keyframes fadeInScale {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          .edital-item {
            background: white;
            border: 1px solid #e9ecef;
            border-radius: 12px;
            padding: 16px 20px;
            transition: all 0.2s;
            animation: fadeInScale 0.3s ease-out;
          }
          .edital-item:hover {
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            transform: translateY(-2px);
          }
        `}
      </style>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out'
        }}
        onClick={handleClose}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)',
            transition: 'all 0.2s ease-in-out'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            padding: '24px 32px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {categoria && (
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#f0f7ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0d6efd'
                }}>
                  <CategoriaIcone categoria={categoria} size={24} showTooltip={false} />
                </div>
              )}
              <div>
                <h5 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>Editais do Processo</h5>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                  <small style={{ color: '#6c757d', fontSize: '13px' }}>{processoNome}</small>
                  {categoria && (
                    <>
                      <span style={{ color: '#dee2e6' }}>•</span>
                      <small style={{ 
                        color: '#0d6efd', 
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {formatarCategoria(categoria)}
                      </small>
                    </>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '0',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              ×
            </button>
          </div>
          <div style={{
            padding: '24px 32px',
            overflowY: 'auto',
            backgroundColor: '#fafafa',
            flex: 1
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {editais.map((editalOrString, index) => {
                const isString = typeof editalOrString === 'string';
                const edital = isString ? { nome: editalOrString } as Edital : editalOrString as Edital;
                const id = edital.id ?? `edital-${index}`;
                const nome = edital.nome ?? 'Sem nome';

                return (
                  <div
                    key={id}
                    className="edital-item"
                    style={{
                      animationDelay: `${index * 0.05}s`
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#0d6efd',
                        flexShrink: 0
                      }} />
                      <span style={{ fontSize: '15px', fontWeight: '500', color: '#212529' }}>
                        {nome}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{
            padding: '20px 32px',
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                border: 'none',
                background: '#f8f9fa',
                color: '#495057',
                padding: '8px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditaisProcessoModal;
